import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { QUERY_KEYS } from '@/constants/query-keys';
import { useInfiniteList } from '@/hooks/useInfiniteList';
import type { UserStatus } from '@/types/schemas';

import {
  createMember,
  createMemberAccount,
  deleteMemberPhoto,
  getMember,
  getMemberAccount,
  getMemberPhoto,
  listMembers,
  type MemberAccountInput,
  type MemberFilter,
  resetMemberPassword,
  updateMember,
  updateMemberAccount,
  uploadMemberPhoto,
} from '../api/members.api';
import type { Member, MemberInput, MemberStatus } from '../schemas/member.schema';

/** Members loaded 20 at a time as the user scrolls the Members table. */
export function useMemberFeed(filter: Omit<MemberFilter, 'page' | 'pageSize'>) {
  return useInfiniteList({
    queryKey: QUERY_KEYS.members.feed({ ...filter }),
    fetchPage: (page, signal) => listMembers({ ...filter, page, pageSize: 20 }, signal),
  });
}

export function useMembers(filter: MemberFilter, enabled = true) {
  return useQuery({
    queryKey: QUERY_KEYS.members.list({ ...filter }),
    queryFn: ({ signal }) => listMembers(filter, signal),
    // Keep the current rows on screen while the next page or search loads.
    placeholderData: keepPreviousData,
    enabled,
  });
}

/** How many members have a status (all members when `status` is undefined). */
/** How many members match — by status, and optionally on a day pass today (`pass`) or not. */
export function useMemberCount(status?: MemberStatus, enabled = true, pass?: 'day' | 'regular') {
  const filter = { page: 1, pageSize: 1, status, pass };
  return useQuery({
    queryKey: QUERY_KEYS.members.list({ ...filter, count: true }),
    queryFn: ({ signal }) => listMembers(filter, signal),
    select: (page) => page.meta.total,
    enabled,
  });
}

export function useMember(memberId: number) {
  return useQuery({
    queryKey: QUERY_KEYS.members.detail(memberId),
    queryFn: ({ signal }) => getMember(memberId, signal),
  });
}

/** Puts a saved member straight into its detail cache, then refreshes every member list. */
function useMemberSaved() {
  const queryClient = useQueryClient();
  return (member: Member) => {
    queryClient.setQueryData(QUERY_KEYS.members.detail(member.id), member);
    return queryClient.invalidateQueries({ queryKey: QUERY_KEYS.members.all });
  };
}

export function useSaveMember() {
  const onSaved = useMemberSaved();
  return useMutation({
    mutationFn: ({ id, input }: { id: number | null; input: Partial<MemberInput> }) =>
      id === null ? createMember(input as MemberInput) : updateMember(id, input),
    onSuccess: onSaved,
  });
}

// --- Photo -------------------------------------------------------------------------------

/**
 * The member's photo as a URL an `<img>` can show, or `null` while there is none (or while it
 * loads). The API serves the image only to signed-in staff, so it is fetched like any other
 * request and turned into a `data:` URL.
 */
export function useMemberPhotoUrl(memberId: number, photoPath: string | null): string | null {
  const photo = useQuery({
    queryKey: QUERY_KEYS.members.photo(memberId, photoPath ?? ''),
    queryFn: ({ signal }) => getMemberPhoto(memberId, signal),
    enabled: photoPath !== null,
    // The key holds the photo's path: while that is the same, the image is too.
    staleTime: Infinity,
  });

  return photo.data ?? null;
}

export function useUploadMemberPhoto(memberId: number) {
  const onSaved = useMemberSaved();
  return useMutation({
    mutationFn: (file: File) => uploadMemberPhoto(memberId, file),
    onSuccess: onSaved,
  });
}

export function useRemoveMemberPhoto(memberId: number) {
  const onSaved = useMemberSaved();
  return useMutation({
    mutationFn: () => deleteMemberPhoto(memberId),
    onSuccess: onSaved,
  });
}

// --- App login ---------------------------------------------------------------------------

export function useMemberAccount(memberId: number, enabled: boolean) {
  return useQuery({
    queryKey: QUERY_KEYS.members.account(memberId),
    queryFn: ({ signal }) => getMemberAccount(memberId, signal),
    enabled,
  });
}

function useInvalidateAccount(memberId: number) {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.members.account(memberId) });
}

export function useCreateMemberAccount(memberId: number) {
  const invalidate = useInvalidateAccount(memberId);
  return useMutation({
    mutationFn: (input: MemberAccountInput) => createMemberAccount(memberId, input),
    onSuccess: invalidate,
  });
}

export function useSetMemberAccountStatus(memberId: number) {
  const invalidate = useInvalidateAccount(memberId);
  return useMutation({
    mutationFn: (status: UserStatus) => updateMemberAccount(memberId, { status }),
    onSuccess: invalidate,
  });
}

export function useResetMemberPassword(memberId: number) {
  const invalidate = useInvalidateAccount(memberId);
  return useMutation({
    mutationFn: () => resetMemberPassword(memberId),
    onSuccess: invalidate,
  });
}
